import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatCount } from '../../api/queueMonitor';
import './TablePager.scss';

export interface ITablePagerProps {
    /** One-based position of the first row on the current page. */
    firstOnPage: number;
    lastOnPage: number;
    total: number;
    /** Zero-based, so it matches the state the table pages with. */
    page: number;
    pageCount: number;
    pageSize: number;
    pageSizes: number[];
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    /** What a row is called in the count: "1-100 van 397 <rowNoun>". */
    rowNoun: string;
    /** A word in brackets behind the count, for a list that is not the whole truth. */
    note?: string;
    /** Spells that note out; renders an 'i' behind the count. */
    hint?: string;
    className?: string;
}

/**
 * The count and the paging of a table, in one bar.
 *
 * Both belong here because they answer the same question - which rows am I looking at,
 * and where are the rest. A count next to the heading instead reads as "this is all there
 * is", while the table underneath it shows a hundred of them.
 */
export const TablePager = (props: ITablePagerProps) => {
    return (
        <div
            className={['table-pager', props.className]
                .filter(Boolean)
                .join(' ')}
        >
            <span className="table-pager__count">
                {formatCount(props.firstOnPage)}&ndash;
                {formatCount(props.lastOnPage)} van {formatCount(props.total)}{' '}
                {props.rowNoun}
                {props.note ? ` (${props.note})` : null}
                {props.hint && (
                    <span
                        className="table-pager__hint"
                        tabIndex={0}
                        role="note"
                        title={props.hint}
                        aria-label={props.hint}
                    >
                        <FontAwesomeIcon icon="circle-info" />
                    </span>
                )}
            </span>

            <div className="btn-group btn-group-sm">
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => props.onPageChange(props.page - 1)}
                    disabled={props.page === 0}
                >
                    Vorige
                </button>
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => props.onPageChange(props.page + 1)}
                    disabled={props.page >= props.pageCount - 1}
                >
                    Volgende
                </button>
            </div>

            <span className="table-pager__page">
                Pagina {props.page + 1} van {props.pageCount}
            </span>

            <select
                className="form-select filter filter--sm"
                value={props.pageSize}
                onChange={(e) => props.onPageSizeChange(Number(e.target.value))}
                aria-label="Regels per pagina"
            >
                {props.pageSizes.map((size) => (
                    <option key={size} value={size}>
                        {size} per pagina
                    </option>
                ))}
            </select>
        </div>
    );
};
